import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMetadataIcons } from '../lib/useMetadataIcons';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { Bell, Calendar, ChevronDown, Check, LogOut, Layers, Filter, Tag } from 'lucide-react';
import CostTrackerWidget from './CostTrackerWidget';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATIC_BRANDS = ['Wellcore', 'YouWeFit', 'Dynamite', 'Osmo', 'Okami', 'Zero Sugar'];

export default function Header({ user, filters, onFiltersChange, pageFilters, showExport, onExport, onImport, monthControl, hideComparison, hideGlobalFilters }) {
  const navigate = useNavigate();
  const { brandIcon, platformIcon } = useMetadataIcons();
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [dateMode, setDateMode] = useState('daily');
  const [selectedMonth, setSelectedMonth] = useState(null);
  const dateRef = useRef(null);

  // Page-specific filter state
  const [selectedChannel, setSelectedChannel] = useState('');
  const [selectedBrands, setSelectedBrands] = useState([]); // multi-select
  const [selectedExtras, setSelectedExtras] = useState([]); // multi-select
  const [thirdGraph, setThirdGraph] = useState('acos');
  const [showBrandDropdown, setShowBrandDropdown] = useState(false);
  const [showExtraDropdown, setShowExtraDropdown] = useState(false);
  const [brandOptions, setBrandOptions] = useState([]);
  const brandRef = useRef(null);
  const extraRef = useRef(null);

  const pf = pageFilters || {};
  const channelOptions = pf.channels || [];
  const extraOptions = pf.extraOptions || [];
  const extraLabel = pf.extraLabel || '';
  const extraKey = pf.extraKey || 'extra';
  const showChannelGate = pf.channelGated === true;
  const isUnlocked = !showChannelGate || selectedChannel !== '';

  const handleLogout = async () => {
    try {
      await fetch(`${API}/auth/logout`, { method: 'POST', credentials: 'include' });
    } catch (err) {
      console.error('Logout error:', err);
      toast.error('Failed to logout');
    } finally {
      try { window.localStorage.removeItem('trace_session_token'); } catch (_) {}
      navigate('/login');
    }
  };

  // Initialize dates — default to the PREVIOUS calendar month
  // (e.g. today 2026-02-13 → 2026-01-01 to 2026-01-31).
  useEffect(() => {
    const today = new Date();
    const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const end = new Date(today.getFullYear(), today.getMonth(), 0); // last day of previous month
    setStartDate(start);
    setEndDate(end);
    setSelectedMonth(start);
    if (onFiltersChange) {
      onFiltersChange(prev => ({
        ...prev,
        start_date: start.toISOString().split('T')[0],
        end_date: end.toISOString().split('T')[0],
        dateMode: 'daily',
        channel: '',
        brands: [],
        extras: [],
        thirdGraph: 'acos',
        isUnlocked: !showChannelGate
      }));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync local date state when parent updates filters (e.g., auto-expand to data range)
  useEffect(() => {
    const sd = filters?.start_date;
    const ed = filters?.end_date;
    if (sd) {
      const d = new Date(sd);
      if (!startDate || startDate.toISOString().split('T')[0] !== sd) setStartDate(d);
    }
    if (ed) {
      const d = new Date(ed);
      if (!endDate || endDate.toISOString().split('T')[0] !== ed) setEndDate(d);
    }
  }, [filters?.start_date, filters?.end_date]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dateRef.current && !dateRef.current.contains(e.target)) setShowDatePicker(false);
      if (brandRef.current && !brandRef.current.contains(e.target)) setShowBrandDropdown(false);
      if (extraRef.current && !extraRef.current.contains(e.target)) setShowExtraDropdown(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const pushUpdate = (updates) => {
    if (!onFiltersChange) return;
    onFiltersChange(prev => ({ ...prev, ...updates }));
  };

  const handleDateModeChange = (mode) => {
    setDateMode(mode);
    pushUpdate({ dateMode: mode });
  };

  const handleDateChange = (dates) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
    if (start && end) {
      pushUpdate({
        start_date: start.toISOString().split('T')[0],
        end_date: end.toISOString().split('T')[0]
      });
      setShowDatePicker(false);
    }
  };

  const handleMonthChange = (date) => {
    setSelectedMonth(date);
    const start = new Date(date.getFullYear(), date.getMonth(), 1);
    const end = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    setStartDate(start);
    setEndDate(end);
    pushUpdate({
      start_date: start.toISOString().split('T')[0],
      end_date: end.toISOString().split('T')[0]
    });
    setShowDatePicker(false);
  };

  const handleChannelChange = (val) => {
    const v = val === 'none' ? '' : val;
    setSelectedChannel(v);
    setSelectedBrands([]);
    setSelectedExtras([]);
    const unlocked = !showChannelGate || v !== '';
    pushUpdate({ channel: v, brands: [], extras: [], isUnlocked: unlocked });
    if (v) {
      fetch(`${API}/ad-properties/brands?channel=${encodeURIComponent(v)}`, { credentials: 'include' })
        .then(r => r.ok ? r.json() : { brands: [] })
        .then(data => {
          const apiBrands = data.brands || [];
          // Whitelist to the 6 canonical brands. Case-insensitive match keeps DB variants
          // (OSMO/Osmo, Youwefit/YouWeFit) under their canonical label. Order = STATIC_BRANDS.
          const apiSetLower = new Set(apiBrands.map(b => String(b).toLowerCase()));
          const filtered = STATIC_BRANDS.filter(b => apiSetLower.has(b.toLowerCase()));
          setBrandOptions(filtered.length > 0 ? filtered : STATIC_BRANDS);
        })
        .catch(() => setBrandOptions(STATIC_BRANDS));
    } else {
      setBrandOptions([]);
    }
  };

  const toggleBrand = (brand) => {
    const next = selectedBrands.includes(brand)
      ? selectedBrands.filter(b => b !== brand)
      : [...selectedBrands, brand];
    setSelectedBrands(next);
    pushUpdate({ brands: next });
  };

  const toggleExtra = (extra) => {
    const next = selectedExtras.includes(extra)
      ? selectedExtras.filter(e => e !== extra)
      : [...selectedExtras, extra];
    setSelectedExtras(next);
    pushUpdate({ extras: next });
  };

  // ── Date presets (Shopify-style) ──
  const _today = () => { const t = new Date(); t.setHours(0,0,0,0); return t; };
  const _addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
  const _firstOf = (yr, mo) => new Date(yr, mo, 1);
  const _lastOf = (yr, mo) => new Date(yr, mo + 1, 0);
  const DATE_PRESETS = [
    { id: 'today', label: 'Today', range: () => { const t = _today(); return [t, t]; } },
    { id: 'yesterday', label: 'Yesterday', range: () => { const y = _addDays(_today(), -1); return [y, y]; } },
    { id: 'last_7', label: 'Last 7 days', range: () => [_addDays(_today(), -6), _today()] },
    { id: 'last_30', label: 'Last 30 days', range: () => [_addDays(_today(), -29), _today()] },
    { id: 'last_90', label: 'Last 90 days', range: () => [_addDays(_today(), -89), _today()] },
    { id: 'mtd', label: 'Month to date', range: () => { const t = _today(); return [_firstOf(t.getFullYear(), t.getMonth()), t]; } },
    { id: 'last_month', label: 'Last month', range: () => { const t = _today(); return [_firstOf(t.getFullYear(), t.getMonth() - 1), _lastOf(t.getFullYear(), t.getMonth() - 1)]; } },
    { id: 'ytd', label: 'Year to date', range: () => { const t = _today(); return [_firstOf(t.getFullYear(), 0), t]; } },
    { id: 'last_year', label: 'Last year', range: () => { const y = _today().getFullYear() - 1; return [_firstOf(y, 0), _lastOf(y, 11)]; } },
  ];
  const COMPARISON_PRESETS = [
    { id: 'none', label: 'No comparison' },
    { id: 'previous_period', label: 'Previous period' },
    { id: 'previous_year', label: 'Previous year' },
    { id: 'custom', label: 'Custom' },
  ];
  const [comparison, setComparison] = useState(filters?.comparison || 'previous_period');
  const [showComparison, setShowComparison] = useState(false);
  const compRef = useRef(null);
  useEffect(() => {
    const handler = (e) => {
      if (compRef.current && !compRef.current.contains(e.target)) setShowComparison(false);
    };
    if (showComparison) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showComparison]);

  const getComparisonLabel = () => {
    if (!startDate || !endDate || comparison === 'none') return 'No comparison';
    const days = Math.round((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
    const fmt = (d) => d.toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' });
    if (comparison === 'previous_period') {
      const cs = _addDays(startDate, -days);
      const ce = _addDays(startDate, -1);
      return `${fmt(cs)} – ${fmt(ce)}`;
    }
    if (comparison === 'previous_year') {
      const cs = new Date(startDate); cs.setFullYear(cs.getFullYear() - 1);
      const ce = new Date(endDate); ce.setFullYear(ce.getFullYear() - 1);
      return `${fmt(cs)} – ${fmt(ce)}`;
    }
    return 'Custom';
  };

  const handleComparisonChange = (id) => {
    setComparison(id);
    setShowComparison(false);
    pushUpdate({ comparison: id });
  };

  const getDateLabel = () => {
    if (dateMode === 'monthly' && selectedMonth) {
      return selectedMonth.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    }
    if (startDate && endDate) {
      const fmt = (d) => d.toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' });
      return `${fmt(startDate)} - ${fmt(endDate)}`;
    }
    return 'Select dates';
  };

  // Compute extra options dynamically based on channel
  const computedExtraOptions = typeof pf.getExtraOptions === 'function'
    ? pf.getExtraOptions(selectedChannel)
    : extraOptions;

  return (
    <header className="sticky top-0 z-10 bg-white border-b border-gray-200">
      {/* Top bar — neutral background, blends with page */}
      <div className="flex items-center justify-end px-6 py-2 bg-white border-b border-gray-100">
        <div className="flex items-center gap-3">
          <CostTrackerWidget />
          <button className="relative h-9 w-9 rounded-full hover:bg-gray-100 transition-colors flex items-center justify-center" data-testid="notifications-bell">
            <Bell className="w-[18px] h-[18px] text-gray-500" />
            <span className="absolute top-2 right-2 w-1.5 h-1.5 bg-red-500 rounded-full"></span>
          </button>
          <div className="flex items-center gap-2 pl-2 pr-3 py-1 rounded-full bg-emerald-50 border border-emerald-200">
            <img src={user?.picture || 'https://via.placeholder.com/32'} alt={user?.name} className="w-6 h-6 rounded-full" />
            <span className="text-xs font-semibold text-emerald-700 tracking-wide">{user?.name?.split(' ')[0]}</span>
          </div>
          <TooltipProvider delayDuration={150}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={handleLogout}
                  data-testid="header-logout-btn"
                  aria-label="Logout"
                  className="h-9 w-9 rounded-full text-gray-500 hover:text-rose-600 hover:bg-rose-50 transition-colors flex items-center justify-center"
                >
                  <LogOut className="w-[18px] h-[18px]" strokeWidth={2} />
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="bg-white text-[#0F0F0F] text-xs px-2 py-1 border border-gray-200">
                Logout
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      {/* Unified filter bar */}
      {!hideGlobalFilters && (
      <div className="px-6 py-3 space-y-2">
        {/* Row 1: Date + Daily/Monthly toggle.
            Date picker is hidden in Monthly mode — month selection happens via
            the "(current)" chip and "+ Add month" picker on the page. */}
        <div className="flex items-center gap-3">
          <div className="relative" ref={dateRef}>
            <button
              onClick={() => setShowDatePicker(!showDatePicker)}
              className="flex items-center gap-2 border border-gray-200 rounded-lg px-3 py-2 bg-white hover:bg-gray-50 transition-colors shadow-sm min-w-[220px]"
              data-testid="date-filter"
            >
              <Calendar className="w-4 h-4 text-[#32B4AC] shrink-0" />
              <span className="text-sm text-gray-700 truncate">{getDateLabel()}</span>
              <ChevronDown className="w-3 h-3 text-gray-400 ml-auto shrink-0" />
            </button>

            {showDatePicker && (
              <div className="absolute top-full mt-1 z-50 bg-white border border-gray-200 rounded-xl shadow-2xl p-0 flex" data-testid="date-range-popover" style={{ minWidth: 720 }}>
                {/* Preset list (Shopify-style) */}
                <div className="border-r border-gray-100 py-2 pr-1 pl-3 flex flex-col gap-0.5" style={{ minWidth: 160 }}>
                  {DATE_PRESETS.map(p => (
                    <button
                      key={p.id}
                      onClick={() => handleDateChange(p.range())}
                      data-testid={`date-preset-${p.id}`}
                      className="text-left text-sm px-3 py-1.5 rounded-md text-gray-700 hover:bg-gray-100"
                    >{p.label}</button>
                  ))}
                </div>
                {/* Calendars + footer */}
                <div className="p-3 flex flex-col">
                  <div data-trace-datepicker>
                    <DatePicker
                      selected={startDate}
                      onChange={handleDateChange}
                      startDate={startDate}
                      endDate={endDate}
                      selectsRange
                      inline
                      monthsShown={2}
                    />
                  </div>
                  <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100">
                    <span className="text-[11px] text-gray-400">Pick a preset or drag across the calendar</span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleDateChange([null, null])}
                        className="px-3 py-1 text-xs text-gray-600 border border-gray-200 rounded hover:bg-gray-50"
                        data-testid="date-range-clear"
                      >Clear</button>
                      <button
                        onClick={() => setShowDatePicker(false)}
                        className="px-3 py-1 text-xs text-white bg-[#0F0F0F] hover:bg-black rounded"
                        data-testid="date-range-apply"
                      >Apply</button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Comparison-period pill (Shopify-style) */}
          {!hideComparison && (
          <div className="relative" ref={compRef}>
            <button
              onClick={() => setShowComparison(v => !v)}
              className="flex items-center gap-2 border border-gray-200 rounded-lg px-3 py-2 bg-white hover:bg-gray-50 transition-colors shadow-sm min-w-[200px]"
              data-testid="comparison-pill"
            >
              <Calendar className="w-4 h-4 text-gray-400 shrink-0" />
              <span className="text-sm text-gray-700 truncate">{getComparisonLabel()}</span>
              <ChevronDown className="w-3 h-3 text-gray-400 ml-auto shrink-0" />
            </button>
            {showComparison && (
              <div className="absolute top-full mt-1 z-50 bg-white border border-gray-200 rounded-lg shadow-xl py-1 min-w-[220px]" data-testid="comparison-popover">
                {COMPARISON_PRESETS.map(c => (
                  <button
                    key={c.id}
                    onClick={() => handleComparisonChange(c.id)}
                    data-testid={`comparison-${c.id}`}
                    className={`w-full text-left text-sm px-3 py-1.5 hover:bg-gray-100 flex items-center justify-between ${comparison === c.id ? 'text-[#0F0F0F] font-medium bg-gray-50' : 'text-gray-700'}`}
                  >
                    {c.label}
                    {comparison === c.id && <Check className="w-3.5 h-3.5 text-[#0F0F0F]" />}
                  </button>
                ))}
              </div>
            )}
          </div>
          )}

          {/* Export/Import for Dashboard */}
          {showExport && (
            <div className="ml-auto flex gap-2">
              <button onClick={onExport} className="flex items-center gap-1.5 px-3 h-9 bg-[#32B4AC] hover:bg-[#2a9d95] text-white text-sm rounded-lg shadow-sm transition-colors" data-testid="export-csv-btn">
                Export CSV
              </button>
              <button onClick={() => document.getElementById('file-upload')?.click()} className="flex items-center gap-1.5 px-3 h-9 border border-gray-200 text-sm rounded-lg hover:bg-gray-50 shadow-sm transition-colors" data-testid="import-btn">
                Import
              </button>
              <input id="file-upload" type="file" accept=".csv" className="hidden" onChange={onImport} />
            </div>
          )}
        </div>

        {/* Row 2: Channel, Brand, Ad Product filters */}
        {(channelOptions.length > 0 || (extraLabel && computedExtraOptions.length > 0)) && (
        <div className="flex items-center gap-3">
          {/* Channel */}
          {channelOptions.length > 0 && (
          <Select value={selectedChannel || 'none'} onValueChange={handleChannelChange}>
            <SelectTrigger className={`w-[160px] h-9 text-sm shadow-sm ${showChannelGate && !selectedChannel ? 'border-amber-300 bg-amber-50' : selectedChannel ? 'border-[#32B4AC]/40 bg-[#32B4AC]/5 text-[#0F0F0F] font-medium' : 'border-gray-200'}`} data-testid="filter-channel">
              {selectedChannel && platformIcon(selectedChannel) && (
                <img src={platformIcon(selectedChannel)} alt="" className="w-4 h-4 rounded object-contain shrink-0 mr-1.5" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
              )}
              <SelectValue placeholder="Channel" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Select Channel</SelectItem>
              {channelOptions.map(ch => (
                <SelectItem key={ch} value={ch}>
                  <span className="inline-flex items-center gap-2">
                    {platformIcon(ch) && <img src={platformIcon(ch)} alt="" className="w-4 h-4 rounded object-contain" onError={(e) => { e.currentTarget.style.display = 'none'; }} />}
                    {ch}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          )}

        {/* Brand — Multi-select */}
        {brandOptions.length > 0 && (
        <div className="relative" ref={brandRef}>
          <button onClick={() => { setShowBrandDropdown(!showBrandDropdown); setShowExtraDropdown(false); }}
            className={`flex items-center gap-1.5 border rounded-lg px-3 h-9 shadow-sm text-sm min-w-[140px] transition-colors ${selectedBrands.length > 0 ? 'border-[#32B4AC]/40 bg-[#32B4AC]/5 hover:bg-[#32B4AC]/10' : 'border-gray-200 bg-white hover:bg-gray-50'}`}
            data-testid="filter-brand">
            {selectedBrands.length > 0 && (
              <div className="flex items-center -space-x-1.5 shrink-0">
                {selectedBrands.slice(0, 3).map(b => (
                  brandIcon(b) ? (
                    <img key={b} src={brandIcon(b)} alt="" className="w-4 h-4 rounded-full border border-white bg-white object-contain" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                  ) : null
                ))}
              </div>
            )}
            <span className={`truncate ${selectedBrands.length > 0 ? 'text-[#0F0F0F] font-medium' : 'text-gray-700'}`}>
              {selectedBrands.length === 0 ? 'Brands' : selectedBrands.length === 1 ? selectedBrands[0] : `${selectedBrands.length} Brands`}
            </span>
            {selectedBrands.length > 1 && <span className="px-1.5 py-0.5 rounded-md bg-[#32B4AC] text-white text-[10px] font-semibold leading-none">{selectedBrands.length}</span>}
            <ChevronDown className="w-3 h-3 text-gray-400 ml-auto shrink-0" />
          </button>
          {showBrandDropdown && (
            <div className="absolute top-full mt-1 z-50 bg-white border border-gray-200 rounded-lg shadow-xl py-1 min-w-[160px]">
              {brandOptions.map(b => (
                <button key={b} onClick={() => toggleBrand(b)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 text-left">
                  <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${selectedBrands.includes(b) ? 'bg-[#32B4AC] border-[#32B4AC]' : 'border-gray-300'}`}>
                    {selectedBrands.includes(b) && <Check className="w-3 h-3 text-white" />}
                  </div>
                  {brandIcon(b) && <img src={brandIcon(b)} alt="" className="w-4 h-4 rounded object-contain shrink-0" onError={(e) => { e.currentTarget.style.display = 'none'; }} />}
                  <span className="text-gray-700">{b}</span>
                </button>
              ))}
              {selectedBrands.length > 0 && (
                <button onClick={() => { setSelectedBrands([]); pushUpdate({ brands: [] }); }}
                  className="w-full px-3 py-2 text-xs text-gray-400 hover:text-gray-600 border-t border-gray-100 text-left">Clear all</button>
              )}
            </div>
          )}
        </div>
        )}

        {/* Extra filter (Ad Product / Stage) — Multi-select */}
        {extraLabel && computedExtraOptions.length > 0 && (
          <div className="relative" ref={extraRef}>
            <button onClick={() => { setShowExtraDropdown(!showExtraDropdown); setShowBrandDropdown(false); }}
              className={`flex items-center gap-1.5 border rounded-lg px-3 h-9 shadow-sm text-sm min-w-[150px] transition-colors ${selectedExtras.length > 0 ? 'border-[#32B4AC]/40 bg-[#32B4AC]/5 hover:bg-[#32B4AC]/10' : 'border-gray-200 bg-white hover:bg-gray-50'}`}
              data-testid="filter-extra">
              <span className={`truncate ${selectedExtras.length > 0 ? 'text-[#0F0F0F] font-medium' : 'text-gray-700'}`}>
                {selectedExtras.length === 0 ? extraLabel : selectedExtras.length === 1 ? selectedExtras[0] : `${selectedExtras.length} selected`}
              </span>
              {selectedExtras.length > 1 && <span className="px-1.5 py-0.5 rounded-md bg-[#32B4AC] text-white text-[10px] font-semibold leading-none">{selectedExtras.length}</span>}
              <ChevronDown className="w-3 h-3 text-gray-400 ml-auto shrink-0" />
            </button>
            {showExtraDropdown && (
              <div className="absolute top-full mt-1 z-50 bg-white border border-gray-200 rounded-lg shadow-xl py-1 min-w-[180px]">
                {computedExtraOptions.map(o => (
                  <button key={o} onClick={() => toggleExtra(o)}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 text-left">
                    <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${selectedExtras.includes(o) ? 'bg-[#32B4AC] border-[#32B4AC]' : 'border-gray-300'}`}>
                      {selectedExtras.includes(o) && <Check className="w-3 h-3 text-white" />}
                    </div>
                    <span className="text-gray-700">{o}</span>
                  </button>
                ))}
                {selectedExtras.length > 0 && (
                  <button onClick={() => { setSelectedExtras([]); pushUpdate({ extras: [] }); }}
                    className="w-full px-3 py-2 text-xs text-gray-400 hover:text-gray-600 border-t border-gray-100 text-left">Clear all</button>
                )}
              </div>
            )}
          </div>
        )}

        </div>
        )}
      </div>
      )}
    </header>
  );
}
